import { useRef, useState, useEffect } from 'react';
import { Button, message, Spin, Popconfirm, Drawer, Form, Tag, Tooltip, Divider } from 'antd';
import Card from 'components/Card/Card';
import { Banner } from 'components/ui/Banner';
import { Add, ChevronDown, ChevronUp, Information, Locked } from '@carbon/icons-react';
import { default as providerService } from 'services/providers.service';
import TaxProfileForm from 'components/TaxProfileForm';
import TaxProfileSummary from 'components/TaxProfileSummary';
import { useFlags } from 'launchdarkly-react-client-sdk';

export default function TaxProfiles({
  organization,
  careFacilities,
  onProfilesChange,
  isEditable = true,
  collapsible,
  open,
  onOpenChange,
  extra,
}) {
  const { providerTaxProfileEditing } = useFlags();
  const [form] = Form.useForm();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedProfiles, setExpandedProfiles] = useState([]);
  const stagedW9Ref = useRef(null);

  useEffect(() => {
    if (organization?.id) {
      fetchProfiles();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const data = await providerService.getTaxProfiles(organization.id);
      setProfiles(data || []);
      onProfilesChange?.(data || []);
    } catch (error) {
      console.error('Error fetching tax profiles:', error);
      message.error('Failed to load tax profiles');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    stagedW9Ref.current = null;
    setEditingProfile(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEditModal = (profile) => {
    stagedW9Ref.current = null;
    setEditingProfile(profile);
    form.setFieldsValue({
      businessLegalName: profile.businessLegalName,
      taxIdNumber: profile.taxIdNumber,
      taxClassification: profile.taxClassification
        ? { id: profile.taxClassification.id, title: profile.taxClassification.title }
        : undefined,
    });
    setDrawerOpen(true);
  };

  const closeModal = () => {
    stagedW9Ref.current = null;
    setDrawerOpen(false);
    setEditingProfile(null);
    form.resetFields();
  };

  const handleSave = async (values) => {
    try {
      setSubmitting(true);
      const dto = {
        businessLegalName: values.businessLegalName,
        taxIdNumber: values.taxIdNumber,
        taxClassification: values.taxClassification ?? null,
      };

      let savedProfile;
      if (editingProfile) {
        savedProfile = await providerService.updateTaxProfile(organization.id, editingProfile.id, dto);
        message.success('Tax profile updated successfully');
      } else {
        savedProfile = await providerService.createTaxProfile(organization.id, dto);
        message.success('Tax profile created successfully');
      }

      if (stagedW9Ref.current) {
        try {
          await providerService.uploadW9(organization.id, savedProfile.id, stagedW9Ref.current);
        } catch (err) {
          message.warning('Profile saved but W-9 upload failed. Please try uploading it again.');
          console.error('W-9 upload failed:', err);
        }
      }

      closeModal();
      await fetchProfiles();
    } catch (error) {
      console.error('Error saving tax profile:', error);
      message.error(error.response?.data?.message || 'Failed to save tax profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (profileId) => {
    try {
      await providerService.deleteTaxProfile(organization.id, profileId);
      message.success('Tax profile deleted successfully');
      await fetchProfiles();
    } catch (error) {
      console.error('Error deleting tax profile:', error);
      if (error.response?.status === 409) {
        message.error('Cannot delete a profile that is currently in use');
      } else {
        message.error('Failed to delete tax profile');
      }
    }
  };

  const toggleProfileExpand = (profileId) => {
    setExpandedProfiles((prev) =>
      prev.includes(profileId) ? prev.filter((id) => id !== profileId) : [...prev, profileId],
    );
  };

  const getProfileLocations = (profile) => {
    if (!careFacilities) return [];
    return careFacilities.filter((facility) => facility.applications?.some((app) => app.taxProfile?.id === profile.id));
  };

  if (loading) {
    return (
      <Card
        title={
          <Banner
            variant="card-title"
            question="Tax profiles"
            guidance="Save your organization's tax details. Add more than one if you need different tax settings across locations"
            className="my-8"
          />
        }
        extra={extra}
        className="mb-8"
        data-testid="taxProfilesCard"
      >
        <div className="flex justify-center py-4">
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Banner
            variant="card-title"
            question="Tax profiles"
            guidance="Save your organization's tax details. Add more than one if you need different tax settings across locations"
            className="my-8"
          />
        }
        extra={extra}
        className="mb-8"
        data-testid="taxProfilesCard"
        collapsible={collapsible}
        open={open}
        onOpenChange={onOpenChange}
      >
        {profiles.length === 0 && isEditable && (
          <Button
            type="link"
            onClick={openCreateModal}
            className="p-0 text-blue-500 hover:text-blue-600"
            data-testid="add-tax-profile-button"
          >
            <Add className="ml-[-5px]" size={18} /> Add a tax profile
          </Button>
        )}

        {profiles.length > 0 && (
          <>
            {profiles.map((profile) => {
              const profileLocations = getProfileLocations(profile);
              const isExpanded = expandedProfiles.includes(profile.id);
              const isProfileLocked = profile.careFacilities?.length > 0;

              return (
                <div key={profile.id} className="border border-solid border-gray-200 rounded-lg mb-4 overflow-hidden">
                  <div className="p-4">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                      <div>
                        <div className="font-semibold text-base flex items-center gap-2 flex-wrap">
                          {profile.businessLegalName}
                          {profile.activeOnOrganization && <Tag color="green">active</Tag>}
                          {!isProfileLocked && !!profile.careFacilities?.length && (
                            <Tooltip title={profile.careFacilities.map((f) => f.name).join(', ')}>
                              <Tag
                                color="blue"
                                className="flex items-center gap-1 cursor-default"
                                style={{ width: 'fit-content' }}
                              >
                                <Information size={14} />
                                used by {profile.careFacilities.length} location
                                {profile.careFacilities.length !== 1 ? 's' : ''}
                              </Tag>
                            </Tooltip>
                          )}
                        </div>

                        <TaxProfileSummary profile={profile} className="mt-2" />
                      </div>

                      {!isProfileLocked && isEditable && providerTaxProfileEditing && (
                        <div className="flex gap-2 justify-end mt-3 pt-3 border-t border-solid border-gray-200 md:mt-0 md:pt-0 md:border-0 md:justify-start">
                          <Button type="link" className="text-blue-500" onClick={() => openEditModal(profile)}>
                            Edit
                          </Button>

                          {!profile.activeOnOrganization && (
                            <Popconfirm
                              title="Delete tax profile"
                              description="Are you sure you want to delete this profile?"
                              onConfirm={() => handleDelete(profile.id)}
                              okText="Delete"
                              okButtonProps={{ danger: true }}
                              cancelText="Cancel"
                            >
                              <Button type="link" danger>
                                Delete
                              </Button>
                            </Popconfirm>
                          )}
                        </div>
                      )}
                    </div>

                    {isProfileLocked && (
                      <>
                        <Divider className="mt-8 mb-4" />

                        <div
                          className="flex items-start gap-2 mt-3 text-sm text-gray-600"
                          data-testid="tax-profile-locked-message"
                        >
                          <Locked size={16} className="mt-0.5 shrink-0" />
                          <span>
                            Locked &mdash; referenced by submitted location. Contact your coordinator to make changes.
                          </span>
                        </div>
                      </>
                    )}

                    {!isProfileLocked && profile.activeOnOrganization && (
                      <>
                        <Divider className="mt-8 mb-4" />

                        <div
                          className="flex items-start gap-2 mt-3 text-sm text-gray-600"
                          data-testid="tax-profile-active-message"
                        >
                          <Locked size={16} className="mt-0.5 shrink-0" />
                          <span>
                            This is your organization&rsquo;s active tax profile and cannot be deleted while in use.
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {profileLocations.length > 0 && (
                    <div className="border-t border-solid border-gray-100">
                      <button
                        className="w-full px-4 py-2 flex items-center gap-1.5 text-sm text-gray-500 hover:bg-gray-50 transition-colors bg-transparent border-0 cursor-pointer text-left"
                        onClick={() => toggleProfileExpand(profile.id)}
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        Locations
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-3">
                          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                            {profileLocations.map((facility) => (
                              <li key={facility.id}>{facility.displayName || `Location ${facility.id}`}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {isEditable && (
              <Button
                type="link"
                onClick={openCreateModal}
                className="p-0 text-blue-500 hover:text-blue-600"
                data-testid="add-tax-profile-button"
              >
                <Add className="ml-[-5px]" size={18} /> Add another tax profile
              </Button>
            )}
          </>
        )}
      </Card>

      <Drawer
        title={editingProfile ? 'Update Tax Profile' : 'Add Tax Profile'}
        placement="right"
        onClose={closeModal}
        open={drawerOpen}
        width={640}
        footer={
          <div className="flex justify-end gap-3">
            <Button size="large" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              loading={submitting}
              data-testid="save-tax-profile-button"
            >
              {editingProfile ? 'Update profile' : 'Save profile'}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <TaxProfileForm
            careFacilities={editingProfile?.careFacilities}
            orgId={organization?.id}
            w9FileId={editingProfile?.w9FileId}
            w9FileName={editingProfile?.w9FileName}
            onW9Staged={(file) => {
              stagedW9Ref.current = file;
            }}
          />
        </Form>
      </Drawer>
    </>
  );
}
